// ─── Export Snapshot Engine ───────────────────────────────────────────────────
// Creates and manages IMMUTABLE thesis export snapshots.
// Once a snapshot is created, its data never changes — exports always use
// a snapshot, never live thesis state.

import { supabase } from '../lib/supabase'
import { sanitizeChapterContent } from './contentSanitizer'
import { buildNumberingRegistries } from './numberingEngine'
import { validateFinalThesis } from './thesisValidator'
import { logEvent, EVENT } from './logger'

// ── Snapshot creation ─────────────────────────────────────────────────────────

/**
 * Freeze the current thesis state into an immutable export snapshot.
 *
 * Steps:
 *  1. Load all thesis data (chapters, FM, figures)
 *  2. Run validation and compute export score
 *  3. Build figure/table registries
 *  4. Sanitize all chapter HTML
 *  5. Build TOC / LOF / LOT entries
 *  6. Persist to thesis_export_snapshots
 *  7. Return the snapshot row
 *
 * @param {string} thesisId
 * @param {{ label?: string, userEmail?: string }} opts
 * @returns {Promise<SnapshotRow>}
 */
export async function createSnapshot(thesisId, { label = '', userEmail = '' } = {}) {
  const t0 = Date.now()

  // 1. Load all data
  const [
    { data: thesis },
    { data: chapters },
    { data: fm },
    { data: dbFigures },
  ] = await Promise.all([
    supabase.from('theses').select('*').eq('id', thesisId).maybeSingle(),
    supabase.from('chapters').select('*').eq('thesis_id', thesisId).order('chapter_number'),
    supabase.from('thesis_front_matter').select('*').eq('thesis_id', thesisId).maybeSingle(),
    supabase.from('thesis_figures').select('*').eq('thesis_id', thesisId).order('display_order'),
  ])

  const sorted = (chapters || []).sort((a, b) => (a.chapter_number || 0) - (b.chapter_number || 0))

  // 2. Validation
  const validation = await validateFinalThesis(thesisId, {
    thesis, chapters: sorted, fm, figures: dbFigures || [],
  })

  // 3. Build registries
  const { figures, tables } = buildNumberingRegistries(sorted, dbFigures || [])

  // 4. Sanitize all chapter HTML
  const chapterHtml = {}
  for (const ch of sorted) {
    chapterHtml[ch.id] = sanitizeChapterContent(ch.content || '')
  }

  // 5. Build TOC / LOF / LOT snapshot entries
  const tocSnapshot = sorted.map(ch => ({
    chapterId:     ch.id,
    chapterNumber: ch.chapter_number,
    title:         ch.title,
    sections:      ch.toc_sections?.sections || [],
  }))

  const lofSnapshot = [...figures.values()].filter(f => f.type !== 'table').map(f => ({
    token:        f.token,
    figureNumber: f.figureNumber,
    displayLabel: f.displayLabel,
    title:        f.title,
    caption:      f.caption,
    chapterNum:   f.chapterNum,
  }))

  const lotSnapshot = [...tables.values()].map(t => ({
    token:       t.token,
    tableNumber: t.tableNumber,
    displayLabel:t.displayLabel,
    title:       t.title,
    caption:     t.caption,
    chapterNum:  t.chapterNum,
  }))

  // 6. Determine next version number
  const { data: existingSnaps } = await supabase
    .from('thesis_export_snapshots')
    .select('version_number')
    .eq('thesis_id', thesisId)
    .order('version_number', { ascending: false })
    .limit(1)

  const versionNumber = ((existingSnaps?.[0]?.version_number) || 0) + 1

  // 7. Persist snapshot
  const snapshotRow = {
    thesis_id:       thesisId,
    version_number:  versionNumber,
    export_status:   validation.passed ? 'READY' : 'DRAFT',
    snapshot_data:   { thesis, fm },
    chapter_html:    chapterHtml,
    figure_registry: Object.fromEntries(figures),
    table_registry:  Object.fromEntries(tables),
    toc_snapshot:    tocSnapshot,
    lof_snapshot:    lofSnapshot,
    lot_snapshot:    lotSnapshot,
    export_score:    validation.exportReadinessScore,
    validation_data: {
      passed:   validation.passed,
      errors:   validation.errors,
      warnings: validation.warnings,
      score:    validation.exportReadinessScore,
    },
    label,
    created_by: userEmail,
  }

  const { data: saved, error } = await supabase
    .from('thesis_export_snapshots')
    .insert(snapshotRow)
    .select()
    .single()

  if (error) throw new Error(`Snapshot creation failed: ${error.message}`)

  await logEvent(EVENT.CREATE_SNAPSHOT, {
    thesisId,
    status:    'OK',
    durationMs: Date.now() - t0,
    metadata:  { versionNumber, score: validation.exportReadinessScore, label },
  })

  return saved
}

// ── Snapshot retrieval ────────────────────────────────────────────────────────

/**
 * List all snapshots for a thesis, newest first.
 */
export async function listSnapshots(thesisId) {
  const { data, error } = await supabase
    .from('thesis_export_snapshots')
    .select('id, thesis_id, version_number, export_status, export_score, label, created_by, created_at')
    .eq('thesis_id', thesisId)
    .order('version_number', { ascending: false })

  if (error) throw error
  return data || []
}

/**
 * Load a full snapshot by ID.
 */
export async function loadSnapshot(snapshotId) {
  const { data, error } = await supabase
    .from('thesis_export_snapshots')
    .select('*')
    .eq('id', snapshotId)
    .maybeSingle()

  if (error) throw error
  return data
}

/**
 * Load the latest snapshot for a thesis.
 */
export async function loadLatestSnapshot(thesisId) {
  const { data, error } = await supabase
    .from('thesis_export_snapshots')
    .select('*')
    .eq('thesis_id', thesisId)
    .order('version_number', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) throw error
  return data
}

// ── Chapter version management ────────────────────────────────────────────────

/**
 * Save a chapter version snapshot (called on every significant save).
 */
export async function saveChapterVersion(thesisId, chapter, { eventType = 'SAVE', userEmail = '' } = {}) {
  // Get next version number for this chapter
  const { data: existing } = await supabase
    .from('thesis_chapter_versions')
    .select('version_num')
    .eq('chapter_id', chapter.id)
    .order('version_num', { ascending: false })
    .limit(1)

  const versionNum = ((existing?.[0]?.version_num) || 0) + 1

  const { error } = await supabase.from('thesis_chapter_versions').insert({
    thesis_id:   thesisId,
    chapter_id:  chapter.id,
    version_num: versionNum,
    content:     chapter.content || '',
    word_count:  chapter.word_count || 0,
    status:      chapter.status || 'DRAFT',
    event_type:  eventType,
    created_by:  userEmail,
  })

  if (error) console.error('[snapshotEngine] Chapter version save failed:', error)
  return versionNum
}

/**
 * List all versions for a chapter.
 */
export async function listChapterVersions(chapterId) {
  const { data, error } = await supabase
    .from('thesis_chapter_versions')
    .select('id, chapter_id, version_num, word_count, status, event_type, created_by, created_at')
    .eq('chapter_id', chapterId)
    .order('version_num', { ascending: false })

  if (error) throw error
  return data || []
}

/**
 * Restore a chapter to a specific version's content.
 * Returns the restored content string.
 */
export async function restoreChapterVersion(chapterId, versionId) {
  const { data: version, error } = await supabase
    .from('thesis_chapter_versions')
    .select('content, word_count, status')
    .eq('id', versionId)
    .maybeSingle()

  if (error || !version) throw new Error('Version not found')

  const { error: updateError } = await supabase
    .from('chapters')
    .update({ content: version.content, word_count: version.word_count, status: version.status })
    .eq('id', chapterId)

  if (updateError) throw updateError
  return version
}

// ── Snapshot data helpers ─────────────────────────────────────────────────────

/**
 * Reconstruct chapter array from a snapshot for export.
 * Returns chapters with their frozen HTML content.
 */
export function snapshotToChapters(snapshot) {
  const { chapter_html, toc_snapshot } = snapshot
  return (toc_snapshot || []).map(entry => ({
    id:             entry.chapterId,
    chapter_number: entry.chapterNumber,
    title:          entry.title,
    content:        chapter_html[entry.chapterId] || '',
    toc_sections:   { sections: entry.sections || [] },
  }))
}

/**
 * Reconstruct figure/table Maps from a snapshot for export.
 */
export function snapshotToRegistries(snapshot) {
  const figures = new Map(Object.entries(snapshot.figure_registry || {}))
  const tables  = new Map(Object.entries(snapshot.table_registry  || {}))
  return { figures, tables }
}
